import { readReceipt, readReceipts } from "@prostocheck/fiscal";
import {
  castErrorResult,
  createSuccessResult,
  IDateRange,
  IFiscalClient,
  IReceipt,
  IReceiptFilters,
  IReceiptWithSource,
  SimpleResult,
  dayjs,
  DATE_FORMAT_YMD,
} from "@prostocheck/shared";

import { toLocalTime } from "./receipts.utils";

export interface IReceiptsQuery extends IDateRange {
  client: IFiscalClient;
  cashRegisterFn: string;
}

/**
 * Receipts of a register over a period, newest first. Expensive: every total
 * costs one document call, so the screen keeps the range narrow — see
 * ai/docs/prro-api.md.
 */
export const fetchReceipts = async ({
  client,
  cashRegisterFn,
  from,
  to,
}: IReceiptsQuery): Promise<SimpleResult<IReceipt[]>> => {
  const result = await readReceipts({
    client,
    registrarFiscalNumber: cashRegisterFn,
    from,
    to,
  });

  if (!result.success) {
    return castErrorResult(result);
  }

  return createSuccessResult(
    [...(result.data ?? [])].sort((a, b) =>
      b.issuedAt.localeCompare(a.issuedAt),
    ),
  );
};

/**
 * One receipt plus the tax cabinet link ДПС itself publishes for it. That link
 * is preferred over building the URL ourselves: it is the source of truth.
 */
export const fetchReceipt = ({
  client,
  cashRegisterFn,
  fiscalNumber,
}: {
  client: IFiscalClient;
  cashRegisterFn: string;
  fiscalNumber: string;
}): Promise<SimpleResult<IReceiptWithSource>> =>
  readReceipt({
    client,
    registrarFiscalNumber: cashRegisterFn,
    fiscalNumber,
  });

/**
 * Time-of-day and type narrowing, applied client-side. The dates already bound
 * the query, so only the finer filters remain.
 */
export const filterReceipts = ({
  receipts,
  filters,
}: {
  receipts: IReceipt[];
  filters: IReceiptFilters;
}): IReceipt[] =>
  receipts.filter(receipt => {
    const issued = new Date(receipt.issuedAt);
    const date = dayjs(issued).format(DATE_FORMAT_YMD);
    const time = toLocalTime(issued);

    if (filters.type !== "all" && receipt.type !== filters.type) return false;
    if (filters.dateFrom && date < filters.dateFrom) return false;
    if (filters.dateTo && date > filters.dateTo) return false;
    if (filters.timeFrom && time < filters.timeFrom) return false;
    if (filters.timeTo && time > filters.timeTo) return false;

    return true;
  });
